// Trix Direct Upload Configuration for Active Storage
// This enables image uploads to persist when switching tabs

import { DirectUpload } from "@rails/activestorage"

// Handle Trix attachment events for direct uploads
document.addEventListener("trix-attachment-add", function(event) {
  const { attachment, target } = event

  // Enforce 1-image limit for rich_text pages
  if (target.dataset.pageType === "rich_text") {
    const existingAttachments = target.querySelectorAll("action-text-attachment")
    if (existingAttachments.length >= 1) {
      attachment.remove()
      alert("Rich text pages support a maximum of 1 image. Use a Gallery page for multiple images.")
      return
    }
  }

  // Only handle file attachments (not URL attachments)
  if (attachment.file) {
    uploadFileAttachment(attachment, target)
  }
})

function uploadFileAttachment(attachment, target) {
  const file = attachment.file
  const key = createStorageKey(file)

  // Show loading indicator
  showUploadingIndicator(true, `Uploading ${file.name}...`)

  // Show progress in Trix
  const upload = new DirectUpload(file, directUploadUrl(), {
    directUploadWillStoreFileWithXHR: (xhr) => {
      // Track upload progress
      xhr.upload.addEventListener("progress", (event) => {
        const progress = (event.loaded / event.total) * 100
        attachment.setUploadProgress(progress)

        // Update loading indicator with progress
        updateUploadingIndicator(`Uploading ${file.name}... ${Math.round(progress)}%`)
      })
    }
  })

  upload.create((error, blob) => {
    if (error) {
      console.error('[trix-direct-upload] Upload failed:', error)

      // Dispatch error event for other controllers to handle
      const errorEvent = new CustomEvent('trix-attachment-error', {
        detail: { attachment: attachment, error: error }
      })
      target.dispatchEvent(errorEvent)

      attachment.remove()
      showUploadingIndicator(false)
      alert(`Upload failed: ${error.message || error}`)
    } else {
      // Check if file needs server-side conversion (DICOM, TIFF)
      if (needsConversion(file)) {
        updateUploadingIndicator(`Converting ${file.name} to JPEG...`)

        convertBlob(blob.signed_id)
          .then(convertedBlob => {
            setAttachmentFromBlob(attachment, convertedBlob, target)
            updateUploadingIndicator(`✓ ${file.name} converted successfully`)
            setTimeout(() => showUploadingIndicator(false), 2000)
          })
          .catch(convError => {
            console.error('[trix-direct-upload] Conversion failed:', convError)
            // Fall back to original blob (will show as file link, not inline image)
            setAttachmentFromBlob(attachment, blob, target)
            updateUploadingIndicator(`⚠ Conversion failed for ${file.name}, saved as original`)
            setTimeout(() => showUploadingIndicator(false), 3000)
          })
      } else {
        // Standard image — set attributes directly
        setAttachmentFromBlob(attachment, blob, target)
        updateUploadingIndicator('✓ Upload complete!')
        setTimeout(() => showUploadingIndicator(false), 2000)
      }
    }
  })
}

function createStorageKey(file) {
  const date = new Date()
  const day = date.toISOString().slice(0,10)
  const name = date.getTime()
  return [ 'tmp', day, name, ''].join('/')
}

function directUploadUrl() {
  return '/rails/active_storage/direct_uploads'
}

// ====================================================================
// CONVERSION AND ATTACHMENT HELPERS
// ====================================================================

// Check if file needs server-side format conversion (DICOM, TIFF)
function needsConversion(file) {
  if (!file) return false
  const type = (file.type || '').toLowerCase()
  if (type === 'application/dicom') return true
  if (type === 'image/tiff' || type === 'image/x-tiff') return true

  const ext = (file.name || '').toLowerCase().split('.').pop()
  return ['dcm', 'dicom', 'tif', 'tiff'].includes(ext)
}

// Call server to convert DICOM/TIFF blob to JPEG
async function convertBlob(signedId) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
  const response = await fetch('/rails/active_storage/convert', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken
    },
    body: JSON.stringify({ signed_id: signedId })
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || `Conversion failed (HTTP ${response.status})`)
  }

  return response.json()
}

// Set Trix attachment attributes from blob data (works for both original and converted blobs)
function setAttachmentFromBlob(attachment, blob, target) {
  const sgid = blob.attachable_sgid || blob.signed_id
  const url = blob.service_url || `/rails/active_storage/blobs/${blob.signed_id}/${blob.filename}`

  attachment.setAttributes({
    url: url,
    href: url,
    sgid: sgid,
    contentType: blob.content_type,
    filename: blob.filename,
    filesize: blob.byte_size
  })

  // Dispatch upload complete event
  const uploadCompleteEvent = new CustomEvent('trix-attachment-upload', {
    detail: { attachment: attachment, blob: blob }
  })
  target.dispatchEvent(uploadCompleteEvent)
}

// ====================================================================
// LOADING INDICATOR HELPERS
// ====================================================================

function showUploadingIndicator(show, message = '') {
  let indicator = document.getElementById('trix-upload-indicator')

  if (show) {
    // Create indicator if it doesn't exist
    if (!indicator) {
      indicator = document.createElement('div')
      indicator.id = 'trix-upload-indicator'
      indicator.className = 'alert alert-info'
      indicator.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: 9999;
        min-width: 300px;
        box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        display: flex;
        align-items: center;
        gap: 10px;
      `
      document.body.appendChild(indicator)
    }

    // Build indicator content safely (message may contain user filenames)
    indicator.textContent = ''
    const spinner = document.createElement('div')
    spinner.className = 'spinner-border spinner-border-sm'
    spinner.setAttribute('role', 'status')
    const srOnly = document.createElement('span')
    srOnly.className = 'sr-only'
    srOnly.textContent = 'Loading...'
    spinner.appendChild(srOnly)
    const msgSpan = document.createElement('span')
    msgSpan.textContent = message
    indicator.appendChild(spinner)
    indicator.appendChild(msgSpan)
    indicator.style.display = 'flex'
  } else {
    // Hide indicator
    if (indicator) {
      indicator.style.display = 'none'
    }
  }
}

function updateUploadingIndicator(message) {
  const indicator = document.getElementById('trix-upload-indicator')
  if (indicator) {
    const span = indicator.querySelector('span')
    if (span) {
      span.textContent = message
    }
  }
}

// ====================================================================
// CHARACTER & LINE COUNTER FOR COMMENT FIELDS
// Add data-max-chars="500" and data-max-lines="15" to a trix-editor to enable.
// ====================================================================

document.addEventListener("trix-initialize", function(event) {
  const editor = event.target
  const maxChars = parseInt(editor.dataset.maxChars, 10)
  const maxLines = parseInt(editor.dataset.maxLines, 10)
  if (!maxChars && !maxLines) return

  // Create counter element below the editor
  const counter = document.createElement('small')
  counter.className = 'form-text text-muted trix-char-counter'
  counter.style.cssText = 'display: block; text-align: right; margin-top: 4px;'
  editor.parentNode.insertBefore(counter, editor.nextSibling)

  // Count visual lines using Trix's own document model.
  // Trix's getDocument().toString() produces plain text with \n for each line break.
  // This is the most reliable way — it matches exactly what the user sees.
  function countLines() {
    const text = editor.editor.getDocument().toString()
    // toString() always ends with a trailing \n, so remove it before splitting
    const trimmed = text.replace(/\n$/, '')
    if (trimmed.length === 0) return 1
    return trimmed.split('\n').length
  }

  function updateCounter() {
    const docText = editor.editor.getDocument().toString()
    const plainText = docText.replace(/\n$/, '')
    const charCount = plainText.replace(/\n/g, '').length
    const lineCount = countLines()

    const charsOver = maxChars ? charCount - maxChars : 0
    const linesOver = maxLines ? lineCount - maxLines : 0
    const isOver = charsOver > 0 || linesOver > 0
    const isWarning = !isOver && ((maxChars && (maxChars - charCount) < 50) || (maxLines && (maxLines - lineCount) < 3))

    let parts = []
    if (maxChars) parts.push(`${charCount}/${maxChars} chars`)
    if (maxLines) parts.push(`${lineCount}/${maxLines} lines`)
    let label = parts.join(' · ')

    if (charsOver > 0) label += ` (${charsOver} chars over)`
    if (linesOver > 0) label += ` (${linesOver} lines over)`
    if (isOver) label += ' — use Additional Pages for longer text'

    counter.textContent = label
    counter.className = isOver
      ? 'form-text text-danger trix-char-counter'
      : isWarning
        ? 'form-text text-warning trix-char-counter'
        : 'form-text text-muted trix-char-counter'
  }

  // Block new lines (Enter key) when at the line limit
  if (maxLines) {
    editor.addEventListener('keydown', function(event) {
      if (event.key === 'Enter') {
        if (countLines() >= maxLines) {
          event.preventDefault()
        }
      }
    })
  }

  updateCounter()
  editor.addEventListener('trix-change', updateCounter)
})
